import React, {useMemo, useState} from "react";
import clsx from "clsx";
import {Divider, FormControlLabel, Grid, Switch} from "@material-ui/core";
import Pagination from "@material-ui/lab/Pagination";
import Skeleton from "@material-ui/lab/Skeleton";
import {shallowEqual, useDispatch, useSelector} from "react-redux";
import {
    cancelReportJob,
    clearPendingFade,
    markPendingFade,
    removeReportJob,
    ReportQueueStatus,
    selectReportQueue,
} from "../../state/slices/ReportJobQueue/ReportJobQueue";
import ReportQueueManagementTable from "./ReportQueueManagementTable/ReportQueueManagementTable";
import Title from "../Title";

// Styles
import {useStyles} from "./ReportQueueManagement.styles";

import {useEnqueueSnackbar} from "../../hooks/useEnqueueSnackbar";
import {FindEntity} from "../../state/slices/entities";
import {selectParentEntity} from "../../state/slices/CoreEntity";
import {useCoreEntityContext} from "../../hooks/useCoreEntitySlice";
import apiClient from "../../auth/apiClient";
import ReportQueueService from "../../services/ReportQueueService";
import {ENTITY_TYPE} from "../../constants";

const ReportQueueManagement = () => {
  const classes = useStyles();
  const enqueueSnackbar = useEnqueueSnackbar();
  const dispatch = useDispatch();
  const reportQueueService = new ReportQueueService(apiClient);
  const useCoreEntitySlice = useCoreEntityContext();
  const [currentPage, setCurrentPage] = useState(1);
  const [deleting, setDeleting] = useState({});
  const [isCancelButtonDisabled, setIsCancelButtonDisabled] = useState({});
  const [showActive, setShowActive] = useState(false);
  const itemLimit = 10;

  const allResults = useSelector(selectReportQueue, shallowEqual);
  const entityID = useSelector((state) =>
    useCoreEntitySlice
      ? state.coreEntities.ContextID
      : state.entities?.ContextID
  , shallowEqual);
  const isLoading = useSelector((state) => state.reportJobQueue.isLoading);

  const filteredJobs = useMemo(() => {
      return (allResults ?? []).filter((item) => {
            if (showActive) {
                return item.rqStatusId === ReportQueueStatus.Processing || !item.rqStatusId;
            }
            return true;
        });
    }, [allResults, showActive]);

  const totalCount = filteredJobs.length;

  const parentEntity = useSelector((state) => {
    if (useCoreEntitySlice) return selectParentEntity(entityID)(state);

    const entity = FindEntity(state.entities?.EntityList ?? [], entityID);
    return FindEntity(state.entities?.EntityList ?? [], entity.parententityid);
  }, shallowEqual);
  
  const handleToggle = (event) => {
    const isChecked = event?.target?.checked;
    setCurrentPage(1);
    setShowActive(isChecked);
  };

  const handleCancel = async (id) => {
    const facilityId = parentEntity.typeid === ENTITY_TYPE.FacilityGroup ? parentEntity.entityid : entityID;
    setIsCancelButtonDisabled((prev) => ({ ...prev, [id]: true }));
    dispatch(markPendingFade(id));
    try {
      await dispatch(cancelReportJob({
          rqId: id,
          rqStatusId: 60,
          entityId: facilityId,
      })).unwrap();

      setDeleting((prev) => ({ ...prev, [id]: true }));
      setTimeout(() => {
        setCurrentPage(1);
        setDeleting((prev) => {
          const copy = { ...prev };
          delete copy[id];
          return copy;
        });
        dispatch(removeReportJob({rqId: id}));
      }, 500);
      enqueueSnackbar("Report job cancelled successfully.", {
        variant: "success",
        tag: "ReportJobCancelled",
      });
    } catch {
      enqueueSnackbar("Unable to cancel the report job, please try again later.", {
        variant: "error",
        tag: "FailedToCancelReportJob",
      });
    } finally {
      setIsCancelButtonDisabled({});
      dispatch(clearPendingFade(id));
    }
  }

  const handleView = async (id) => {
    try {
      const reportUrl = await reportQueueService.getQueuedReportUrl(id);
      if(reportUrl?.data) {
        window.open(reportUrl.data, "_blank");
      }
    } catch {
      enqueueSnackbar("Unable to view report please try again later.", {
        variant: "error",
        tag: "FailedToCancelReportJob",
      });
    }
  }

  return (
    <Grid container className={clsx(classes.root)}>
      <Grid item xs={12} className={clsx(classes.header)}>
        <Title className={classes.title}>Report Queue Management</Title>
      </Grid>
      <Grid container className={clsx(classes.infoContainer)}>
        <div className={clsx(["toggle-btn-header", classes.headerButtons])}>
            <FormControlLabel
              control={
                <Switch
                  onChange={handleToggle}
                  name="checkedB"
                  color="primary"
                />
              }
              label={"Only Show Active"}
            />
          </div>
        <Grid item xs={12}>
          {isLoading ? (
            <div className={clsx(classes.loadingWrapper)}>
              {[...Array(3)].map((_, index) => (
                <Skeleton
                  key={index}
                  className={classes.skeleton}
                  animation="wave"
                />
              ))}
            </div>
          ) : (
            <>
              <Divider className={classes.resultsDivider} />
              {totalCount === 0 && (
                <div
                  className={clsx([
                    "animate__animated",
                    "animate__flipInX",
                    "results",
                    "not-found",
                    classes.noResults,
                  ])}
                >
                  <span className={clsx(["no-results-text"])}>
                    No report queue records
                  </span>
                </div>
              )}
              {totalCount > 0 && (
                <Grid
                  container
                  item
                  spacing={2}
                  xs={12}
                  className={clsx(classes.tableContainer)}
                >
                  {Math.ceil(totalCount / itemLimit) > 1 && (
                  <Pagination
                    className={clsx(classes.pagination)}
                    count={Math.ceil(totalCount / itemLimit)}
                    onChange={(_, value) => setCurrentPage(value)}
                    page={currentPage}
                    shape="rounded"
                    color="primary"
                  /> )}             
                  <ReportQueueManagementTable
                    results={filteredJobs.slice(
                      (currentPage - 1) * itemLimit,
                      currentPage * itemLimit
                    )}
                    onCancel={handleCancel}
                    onView={handleView}
                    deleting={deleting}
                    isCancelButtonDisabled={isCancelButtonDisabled}
                  />
                </Grid>
              )}
            </>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
};

export default ReportQueueManagement;
